import type { QueryClient } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSubgraphConfig, SubgraphType } from 'config/subgraph'
import { fetchBlockMap } from 'hooks/backend/blocks'
import { useConnection } from 'hooks/useConnection'
import { capBlocksToLatestIndexedBlock } from 'hooks/useSubgraphLatestIndexedBlock'
import { useEffect, useMemo, useState } from 'react'
import { UnifiedTokenData } from 'types/analytics'
import { buildSelectionKey, dedupeIds, toNumber } from 'utils/analytics'
import { get2DayChange, getPercentChange } from 'utils/analytics/data'
import { useDeltaTimestamps } from 'utils/queries'

import type {
  GetV2TokensByIdsQuery,
  GetV2TokensByIdsQueryVariables,
  GetV2TokensSnapshotsQuery,
  GetV2TopTokensQuery,
} from '../../queries/analytics/v2/generated'
import {
  useGetV2TokensByIdsQuery,
  useGetV2TokensSnapshotsQuery,
  useGetV2TopTokensQuery,
} from '../../queries/analytics/v2/generated'

type TokenSnapshot = {
  id: string
  symbol: string
  name: string
  derivedETH: number
  tradeVolumeUSD: number
  totalLiquidity: number
  priceUSD: number
  tvlUSD: number
}

type GraphV2Token =
  | GetV2TopTokensQuery['tokens'][number]
  | GetV2TokensByIdsQuery['tokens'][number]
  | GetV2TokensSnapshotsQuery['block24'][number]
type GraphV2Bundle = GetV2TopTokensQuery['bundles'][number] | GetV2TokensByIdsQuery['bundles'][number]

const v2TokensBaseQueryKey = (chainId: number, selectionKey: string) =>
  ['analytics', 'v2Tokens', chainId, 'base', selectionKey] as const

export const v2TokensMetricsQueryKey = (
  chainId: number,
  selectionKey: string,
  t24: number,
  t48: number,
  tWeek: number,
  latestIndexedBlockNumber?: number
) =>
  [
    'analytics',
    'v2Tokens',
    chainId,
    'metrics',
    selectionKey,
    t24,
    t48,
    tWeek,
    latestIndexedBlockNumber ?? 'latest',
  ] as const

export const v2TokensQueryKey = (chainId: number, selectionKey: string, t24: number, t48: number, tWeek: number) =>
  ['analytics', 'v2Tokens', chainId, 'full', selectionKey, t24, t48, tWeek] as const

const parseTokenSnapshot = (tokens: GraphV2Token[], ethPriceRaw?: GraphV2Bundle['ethPriceUSD']): TokenSnapshot[] => {
  const ethPrice = ethPriceRaw ? Number(ethPriceRaw) : 0

  return tokens
    .filter((token): token is GraphV2Token => Boolean(token))
    .map((token) => {
      const derivedETH = toNumber(token.derivedETH ?? undefined)
      const priceUSD = ethPrice ? derivedETH * ethPrice : 0
      const totalLiquidity = toNumber(token.totalLiquidityV2 ?? undefined)

      return {
        id: token.id,
        symbol: token.symbol ?? '',
        name: token.name ?? '',
        derivedETH,
        tradeVolumeUSD: toNumber(token.volumeUSD ?? undefined),
        totalLiquidity,
        priceUSD,
        tvlUSD: totalLiquidity * priceUSD,
      }
    })
}

const mapSnapshotsById = (snapshots: TokenSnapshot[]) =>
  snapshots.reduce<Record<string, TokenSnapshot>>((acc, snapshot) => {
    acc[snapshot.id.toLowerCase()] = snapshot
    return acc
  }, {})

export const dedupeV2TokenIds = (ids?: string[]) => dedupeIds(ids)

export const buildV2TokenSelectionKey = (normalizedTokenIds?: string[]) => buildSelectionKey(normalizedTokenIds)

interface V2TokenMetrics
  extends Pick<
    UnifiedTokenData,
    'volumeUSD' | 'volumeUSDChange' | 'volumeUSDWeek' | 'priceUSDChange' | 'priceUSDChangeWeek' | 'fees24hUSD'
  > {
  metricsLoaded: boolean
  metrics24hLoaded: boolean
  metrics7dLoaded: boolean
}

const computeV2TokenMetrics = (
  current: TokenSnapshot | undefined,
  oneDay: TokenSnapshot | undefined,
  twoDays: TokenSnapshot | undefined,
  week: TokenSnapshot | undefined,
  resolved: { day: boolean; week: boolean },
  previous?: V2TokenMetrics
): V2TokenMetrics => {
  const hasCurrent = Boolean(current)
  const metrics24hLoaded = resolved.day && hasCurrent
  const hasWeeklySnapshot = resolved.week && hasCurrent && Boolean(week)
  const metrics7dLoaded = (resolved.week && hasCurrent) || previous?.metrics7dLoaded || false

  if (!current) {
    return {
      volumeUSD: 0,
      volumeUSDChange: 0,
      volumeUSDWeek: previous?.volumeUSDWeek ?? 0,
      priceUSDChange: 0,
      priceUSDChangeWeek: previous?.priceUSDChangeWeek ?? 0,
      fees24hUSD: 0,
      metricsLoaded: metrics24hLoaded && metrics7dLoaded,
      metrics24hLoaded,
      metrics7dLoaded,
    }
  }

  const [computedVolume24h, computedVolumeChange] =
    current && oneDay && twoDays
      ? get2DayChange(
          current.tradeVolumeUSD.toString(),
          oneDay.tradeVolumeUSD.toString(),
          twoDays.tradeVolumeUSD.toString()
        )
      : [oneDay ? Number(current.tradeVolumeUSD) - Number(oneDay.tradeVolumeUSD) : Number(current.tradeVolumeUSD), 0]

  const volume7d = week ? Number(current.tradeVolumeUSD) - Number(week.tradeVolumeUSD) : Number(current.tradeVolumeUSD)

  const normalizedVolumeUSD = Number.isFinite(computedVolume24h) ? Math.max(computedVolume24h, 0) : 0
  const normalizedVolumeUSDWeek = hasWeeklySnapshot
    ? Number.isFinite(volume7d)
      ? Math.max(volume7d, 0)
      : 0
    : previous?.volumeUSDWeek ?? (Number.isFinite(volume7d) ? Math.max(volume7d, 0) : 0)
  const normalizedVolumeUSDChange = Number.isFinite(computedVolumeChange) ? computedVolumeChange : 0

  const priceUSDChange = getPercentChange(current.priceUSD?.toString(), oneDay?.priceUSD?.toString())

  const priceUSDChangeWeek = hasWeeklySnapshot
    ? getPercentChange(current.priceUSD?.toString(), week?.priceUSD?.toString())
    : previous?.priceUSDChangeWeek ?? getPercentChange(current.priceUSD?.toString(), week?.priceUSD?.toString())

  return {
    volumeUSD: normalizedVolumeUSD,
    volumeUSDChange: normalizedVolumeUSDChange,
    volumeUSDWeek: normalizedVolumeUSDWeek,
    priceUSDChange,
    priceUSDChangeWeek,
    fees24hUSD: normalizedVolumeUSD * 0.003,
    metricsLoaded: metrics24hLoaded && metrics7dLoaded,
    metrics24hLoaded,
    metrics7dLoaded,
  }
}

const fetchCurrentV2Tokens = async (
  _chainId: number,
  endpoint: string,
  normalizedTokenIds?: string[]
): Promise<TokenSnapshot[]> => {
  if (normalizedTokenIds && normalizedTokenIds.length === 0) {
    return []
  }

  if (normalizedTokenIds) {
    const variables: GetV2TokensByIdsQueryVariables = { ids: normalizedTokenIds }
    const response = await useGetV2TokensByIdsQuery.fetcher({ endpoint }, variables)()

    const ethPrice = response.bundles?.[0]?.ethPriceUSD
    return parseTokenSnapshot(response.tokens ?? [], ethPrice)
  }

  const response = await useGetV2TopTokensQuery.fetcher({ endpoint }, undefined)()

  return parseTokenSnapshot(response.tokens ?? [], response.bundles?.[0]?.ethPriceUSD)
}

type FetchV2TokenMetricsOptions = {
  providedBlocks?: Map<number, number>
  previousMetrics?: Record<string, V2TokenMetrics>
  latestIndexedBlockNumber?: number
}

type SharedMetricsOptions = {
  blocks?: Map<number, number>
  timestamps?: [number, number, number]
  blocksRequired?: boolean
  latestIndexedBlockNumber?: number
}

const fetchV2TokenMetrics = async (
  chainId: number,
  endpoint: string,
  currentTokens: TokenSnapshot[],
  normalizedTokenIds: string[] | undefined,
  t24: number,
  t48: number,
  tWeek: number,
  options?: FetchV2TokenMetricsOptions
): Promise<Record<string, V2TokenMetrics>> => {
  if (!currentTokens.length) {
    return {}
  }

  const { providedBlocks, previousMetrics, latestIndexedBlockNumber } = options ?? {}

  const tokenIdsToProcess = normalizedTokenIds
    ? normalizedTokenIds
    : currentTokens.map((token) => token.id.toLowerCase())

  const timestamps = [t24, t48, tWeek]
  const uncappedBlocks = providedBlocks ?? (await fetchBlockMap({ chainId, timestamps }))
  const blocks = capBlocksToLatestIndexedBlock(uncappedBlocks, latestIndexedBlockNumber)

  const block24 = blocks.get(t24)
  const block48 = blocks.get(t48)
  const blockWeek = blocks.get(tWeek)

  let oneDaySnapshots: TokenSnapshot[] = []
  let twoDaySnapshots: TokenSnapshot[] = []
  let weekSnapshots: TokenSnapshot[] | undefined

  if (tokenIdsToProcess.length > 0 && (block24 !== undefined || block48 !== undefined || blockWeek !== undefined)) {
    const variables = {
      ids: tokenIdsToProcess,
      block24: block24 ? { number: block24 } : undefined,
      block48: block48 ? { number: block48 } : undefined,
      blockWeek: blockWeek ? { number: blockWeek } : undefined,
    }

    const snapshots = await useGetV2TokensSnapshotsQuery.fetcher({ endpoint }, variables)()

    if (block24 !== undefined) {
      const bundlePrice = snapshots.block24Bundle?.[0]?.ethPriceUSD
      oneDaySnapshots = parseTokenSnapshot(snapshots.block24 ?? [], bundlePrice)
    }

    if (block48 !== undefined) {
      const bundlePrice = snapshots.block48Bundle?.[0]?.ethPriceUSD
      twoDaySnapshots = parseTokenSnapshot(snapshots.block48 ?? [], bundlePrice)
    }

    if (blockWeek !== undefined) {
      const bundlePrice = snapshots.blockWeekBundle?.[0]?.ethPriceUSD
      weekSnapshots = parseTokenSnapshot(snapshots.blockWeek ?? [], bundlePrice)
    }
  }

  const currentById = mapSnapshotsById(currentTokens)
  const oneDayById = mapSnapshotsById(oneDaySnapshots)
  const twoDayById = mapSnapshotsById(twoDaySnapshots)
  const dayResolved = true

  const computeMetrics = (
    weekById?: Record<string, TokenSnapshot>,
    weekResolved = false,
    previousOverride?: Record<string, V2TokenMetrics>
  ) =>
    tokenIdsToProcess.reduce<Record<string, V2TokenMetrics>>((acc, tokenId) => {
      const id = tokenId.toLowerCase()
      const current = currentById[id]
      const oneDay = oneDayById[id]
      const twoDay = twoDayById[id]
      const week = weekById?.[id]

      acc[id] = computeV2TokenMetrics(
        current,
        oneDay,
        twoDay,
        week,
        {
          day: dayResolved,
          week: weekResolved,
        },
        previousOverride?.[id] ?? previousMetrics?.[id]
      )
      return acc
    }, {})

  const weekById = weekSnapshots ? mapSnapshotsById(weekSnapshots) : undefined
  const weekResolved = Boolean(blockWeek && weekSnapshots)
  const metrics = computeMetrics(weekById, weekResolved, previousMetrics)

  return metrics
}

const createV2TokenData = (token: TokenSnapshot, overrides?: Partial<UnifiedTokenData>): UnifiedTokenData => {
  const base: UnifiedTokenData = {
    id: token.id,
    address: token.id,
    name: token.name,
    symbol: token.symbol,
    derivedETH: token.derivedETH,
    priceUSD: token.priceUSD,
    priceUSDChange: 0,
    priceUSDChangeWeek: 0,
    volumeUSD: 0,
    volumeUSDChange: 0,
    volumeUSDWeek: 0,
    tvlToken: token.totalLiquidity,
    tvlUSD: token.tvlUSD,
    fees24hUSD: 0,
    tvl24hUSDChange: 0,
    metricsLoaded: false,
    metrics24hLoaded: false,
    metrics7dLoaded: false,
  }

  if (overrides) {
    return {
      ...base,
      ...overrides,
    }
  }

  return base
}

export const prefetchV2TopTokens = async (
  queryClient: QueryClient,
  chainId: number,
  t24: number,
  t48: number,
  tWeek: number,
  blocks?: Map<number, number>,
  latestIndexedBlockNumber?: number
) => {
  let endpoint: string
  try {
    endpoint = getSubgraphConfig(chainId, SubgraphType.V2).endpoint
  } catch (error) {
    console.error(error)
    return
  }

  const selectionKey = buildV2TokenSelectionKey(undefined)

  const baseTokens =
    (await queryClient.ensureQueryData({
      queryKey: v2TokensBaseQueryKey(chainId, selectionKey),
      queryFn: () => fetchCurrentV2Tokens(chainId, endpoint, undefined),
      staleTime: 1000 * 60 * 5, // 5 minutes
    })) ?? []

  await queryClient.prefetchQuery({
    queryKey: v2TokensMetricsQueryKey(chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber),
    queryFn: () =>
      fetchV2TokenMetrics(chainId, endpoint, baseTokens, undefined, t24, t48, tWeek, {
        providedBlocks: blocks,
        latestIndexedBlockNumber,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

type UseV2TokenDataOptions = SharedMetricsOptions & {
  enabled?: boolean
}

export const useV2TokenDatas = (tokenIds?: string[], options?: UseV2TokenDataOptions) => {
  const defaultTimestamps = useDeltaTimestamps()
  const [t24, t48, tWeek] = options?.timestamps ?? defaultTimestamps
  const providedBlocks = options?.blocks
  const latestIndexedBlockNumber = options?.latestIndexedBlockNumber
  const enabled = (options?.enabled ?? true) && (tokenIds === undefined || tokenIds.length > 0)
  const { chainId } = useConnection()
  const endpoint = useMemo(() => getSubgraphConfig(chainId, SubgraphType.V2).endpoint, [chainId])
  const queryClient = useQueryClient()
  const [hasRefetchedEmptyBase, setHasRefetchedEmptyBase] = useState(false)

  const normalizedTokenIds = useMemo(() => dedupeV2TokenIds(tokenIds), [tokenIds])
  const selectionKey = useMemo(() => buildV2TokenSelectionKey(normalizedTokenIds), [normalizedTokenIds])
  const baseQueryKey = useMemo(() => v2TokensBaseQueryKey(chainId, selectionKey), [chainId, selectionKey])

  const isTopQuery = normalizedTokenIds === undefined
  const tokenIdsForQuery = normalizedTokenIds ?? []

  const topTokensQuery = useGetV2TopTokensQuery<GetV2TopTokensQuery>(
    {
      endpoint,
    },
    undefined,
    {
      enabled: enabled && isTopQuery,
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2TopTokensQuery) => response,
    }
  )

  const tokensByIdsQuery = useGetV2TokensByIdsQuery<GetV2TokensByIdsQuery>(
    {
      endpoint,
    },
    {
      ids: tokenIdsForQuery,
    },
    {
      enabled: enabled && !isTopQuery && tokenIdsForQuery.length > 0,
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2TokensByIdsQuery) => response,
    }
  )

  const baseQuery = isTopQuery ? topTokensQuery : tokensByIdsQuery

  const baseTokenSnapshots = useMemo(() => {
    const source = isTopQuery ? topTokensQuery.data : tokensByIdsQuery.data
    if (!source) return undefined
    const tokens = source.tokens ?? []
    const ethPrice = source.bundles?.[0]?.ethPriceUSD
    return parseTokenSnapshot(tokens, ethPrice)
  }, [isTopQuery, topTokensQuery.data, tokensByIdsQuery.data])

  const baseDataLength = baseTokenSnapshots?.length ?? 0
  const deferEmptyResult =
    (!baseTokenSnapshots && baseQuery.isFetching) || (baseDataLength === 0 && baseQuery.isFetching)

  useEffect(() => {
    if (!enabled) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (!baseQuery.isFetched || baseQuery.isFetching) return

    if (baseDataLength > 0) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (hasRefetchedEmptyBase) return

    setHasRefetchedEmptyBase(true)
    void baseQuery.refetch()
  }, [enabled, baseDataLength, baseQuery, hasRefetchedEmptyBase])

  useEffect(() => {
    setHasRefetchedEmptyBase(false)
  }, [selectionKey])

  const metricsQueryKey = useMemo(
    () => v2TokensMetricsQueryKey(chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber),
    [chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber]
  )

  const metricsQuery = useQuery({
    queryKey: metricsQueryKey,
    queryFn: () => {
      const previousMetrics = queryClient.getQueryData<Record<string, V2TokenMetrics>>(metricsQueryKey)
      return fetchV2TokenMetrics(chainId, endpoint, baseTokenSnapshots ?? [], normalizedTokenIds, t24, t48, tWeek, {
        providedBlocks,
        previousMetrics,
        latestIndexedBlockNumber,
      })
    },
    enabled: enabled && baseDataLength > 0 && (options?.blocksRequired ?? true),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const metricsData = metricsQuery.data

  const baseTokens = useMemo(() => {
    if (deferEmptyResult || !baseTokenSnapshots) return undefined
    if (!baseDataLength) return undefined
    return baseTokenSnapshots.map((token) => createV2TokenData(token))
  }, [baseTokenSnapshots, baseDataLength, deferEmptyResult])

  const combinedData = useMemo(() => {
    if (!baseTokens || !metricsData) return undefined
    const metricsById = metricsData

    return baseTokens.map((token) => {
      const metrics = metricsById[token.id.toLowerCase()]
      return metrics ? { ...token, ...metrics } : token
    })
  }, [baseTokens, metricsData])

  const refetch = async () => {
    const baseResult = await baseQuery.refetch({ cancelRefetch: false })
    if (!metricsQuery.isPaused) {
      await metricsQuery.refetch({ cancelRefetch: false })
    }
    return baseResult
  }

  const hasCombinedData = combinedData !== undefined

  return {
    data: combinedData,
    isLoading: (baseQuery.isLoading || metricsQuery.isLoading) && !hasCombinedData,
    isFetching: baseQuery.isFetching || metricsQuery.isFetching,
    error: baseQuery.error ?? metricsQuery.error,
    refetch,
  }
}
